import { collectionOptions } from "@tanstack/db";
import { hashKey, type QueryClient } from "@tanstack/query-core";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { apiGet, apiPatch } from "@softmax/observatory-client/client";
import type { PlayerResponse } from "@softmax/observatory-client/types";

export function myPlayersKey({
  viewerId,
  elevatedPrivileges,
}: {
  viewerId: string;
  elevatedPrivileges: boolean;
}) {
  return ["players", "mine", { viewerId, elevatedPrivileges }] as const;
}

export function myPlayersCollection({
  viewerId,
  elevatedPrivileges,
  initialData,
}: {
  viewerId: string;
  elevatedPrivileges: boolean;
  initialData?: PlayerResponse[];
}) {
  const queryKey = myPlayersKey({ viewerId, elevatedPrivileges });

  return collectionOptions(hashKey(queryKey), (db) => {
    const queryClient = db.requireDependency<QueryClient>("queryClient");

    return queryCollectionOptions({
      queryClient,
      queryKey,
      queryFn: ({ signal }) =>
        apiGet<PlayerResponse[]>("/players", { signal, elevatedPrivileges }),
      getKey: (player) => player.id,
      initialData,
      staleTime: 30_000,
      onUpdate: async ({ transaction, collection }) => {
        const signal = db.requireDependency<AbortController>("lifetime").signal;
        signal.throwIfAborted();
        await queryClient.cancelQueries({ queryKey, exact: true });
        const players = await Promise.all(
          transaction.mutations.map((mutation) =>
            apiPatch<PlayerResponse>(
              `/players/${encodeURIComponent(mutation.key)}`,
              { name: mutation.modified.name },
              { elevatedPrivileges, signal },
            ).then(undefined, (error: Error) => {
              db.requireDependency<(error: Error) => void>(
                "reportAuthorizationError",
              )(error);
              throw error;
            }),
          ),
        );
        signal.throwIfAborted();
        await queryClient.cancelQueries({ queryKey, exact: true });
        signal.throwIfAborted();
        await collection.utils.writeUpdate(
          players.map((player) => ({ id: player.id, name: player.name })),
        );
      },
    });
  });
}
